import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { ChevronLeft, ChevronRight, Newspaper, CalendarDays } from 'lucide-react';
import { apiCall } from './api';

export default function BlogPage({ onBack, lang = 'fr' }) {
  const fr = lang !== 'en';
  const [posts, setPosts] = useState([]);
  const [active, setActive] = useState(null);

  useEffect(() => { apiCall('/blog').then(setPosts).catch(() => {}); }, []);

  const open = async (slug) => {
    const p = await apiCall(`/blog/${slug}`).catch(() => null);
    if (p) { setActive(p); window.scrollTo({ top: 0, behavior: 'smooth' }); }
  };

  const T = (p, key) => (fr ? p[`${key}_fr`] : p[`${key}_en`]);

  if (active) {
    const body = fr ? active.body_fr : active.body_en;
    return (
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="max-w-3xl mx-auto px-4 py-10" data-testid="blog-article">
        <button onClick={() => setActive(null)} className="text-white/60 hover:text-white text-sm mb-5 flex items-center gap-1" data-testid="blog-back-list">
          <ChevronLeft size={16} />{fr ? 'Tous les articles' : 'All articles'}
        </button>
        <img src={active.image} alt="" className="w-full rounded-2xl mb-6 border border-white/10" data-testid="blog-article-image" />
        <div className="flex items-center gap-2 text-xs text-emerald-300 mb-3">
          <CalendarDays size={13} />{active.date} · {active.author}
        </div>
        <h1 className="text-3xl sm:text-4xl font-black leading-tight">{T(active, 'title')}</h1>
        <p className="text-white/70 mt-3 text-base">{T(active, 'subtitle')}</p>
        <div className="mt-6 space-y-4 text-white/85 leading-relaxed text-[15px]">
          {body.map((para, i) => (
            <p key={i} className={i === body.length - 1 ? 'font-semibold text-emerald-200 pt-2 border-t border-white/10' : ''}>{para}</p>
          ))}
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="max-w-4xl mx-auto px-4 py-10" data-testid="blog-page">
      <button onClick={onBack} className="text-white/60 hover:text-white text-sm mb-5 flex items-center gap-1" data-testid="blog-back">
        <ChevronLeft size={16} />{fr ? 'Retour' : 'Back'}
      </button>
      <div className="flex items-center gap-2 mb-1">
        <Newspaper size={22} className="text-emerald-300" />
        <h1 className="text-3xl sm:text-4xl font-black">Blog</h1>
      </div>
      <p className="text-white/60 mb-8">{fr ? 'Conseils, idées et actualités pour progresser en anglais.' : 'Tips, ideas and news to make progress in English.'}</p>

      <div className="grid sm:grid-cols-2 gap-5">
        {posts.map((p) => (
          <motion.button key={p.slug} whileHover={{ y: -4 }} onClick={() => open(p.slug)} data-testid={`blog-card-${p.slug}`}
            className="text-left rounded-2xl overflow-hidden bg-white/5 border border-white/10 hover:border-emerald-400/40 transition group">
            <img src={p.image} alt="" className="w-full h-44 object-cover" />
            <div className="p-5">
              <div className="text-xs text-emerald-300 mb-2 flex items-center gap-1.5"><CalendarDays size={12} />{p.date}</div>
              <h3 className="font-bold text-lg leading-snug">{T(p, 'title')}</h3>
              <p className="text-white/60 text-sm mt-2 line-clamp-2">{T(p, 'subtitle')}</p>
              <span className="inline-flex items-center gap-1 mt-3 text-sm font-semibold text-emerald-300">
                {fr ? 'Lire l\'article' : 'Read the article'}<ChevronRight size={14} className="group-hover:translate-x-1 transition" />
              </span>
            </div>
          </motion.button>
        ))}
      </div>
    </motion.div>
  );
}
